<template>
  <v-card>
    <v-dialog v-model="uploadDialog" width="500" persistent>
      <v-card>
        <v-card-title
          class="grey lighten-2"
          primary-title
        >
          {{ $t('components.ModelSettings.uploadingLogo') }}
        </v-card-title>

        <v-card-text v-if="!!file && !! uploadFileProgress">
          <v-progress-linear v-model="uploadFileProgress.percent" height="25" reactive class="mt-5">
            <strong>{{ uploadFileProgress.percent }}% ({{ `${fileSizeToString(uploadFileProgress.size)}/${fileSizeToString(file.size)}` }})</strong>
          </v-progress-linear>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="uploadFileProgress.task.cancel(); uploadDialog = false"
          >
            {{ $t('components.ModelSettings.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addValueDialog" width="400" persistent>
      <v-card>
        <v-card-title class="grey lighten-2" primary-title>
          {{ $t('components.ModelSettings.addValueTitle') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="addValueText"
            :label="addValueField ? addValueField.label : ''"
            autofocus
            @keyup.enter="confirmAddValue"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addValueDialog = false">{{ $t('components.ModelSettings.cancel') }}</v-btn>
          <v-btn color="primary" text :disabled="!addValueText || !addValueText.trim()" @click="confirmAddValue">{{ $t('components.ModelSettings.addValueButton') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click.stop="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('components.ModelSettings.modelSettings') }} {{ client.name }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click.stop="save">{{ $t('components.ModelSettings.save') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-row v-if="error">
      <v-col>
        <v-alert text prominent type="error">{{ error }}</v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form ref="form" v-model="validForm">
          <v-card-text>
            <v-container>
              <v-tabs v-model="activeTab">
                <v-tab>{{ $t('components.ModelSettings.displayTab') }}</v-tab>
                <v-tab>{{ $t('components.ModelSettings.customNamesTab') }}</v-tab>
                <v-tab>{{ $t('components.ModelSettings.metadataTab') }}</v-tab>
                <v-tab>{{ $t('components.ModelSettings.channelTaxonomyTab') }}</v-tab>
              </v-tabs>
              <v-tabs-items v-model="activeTab">
                <v-tab-item>
                  <v-row class="mt-2">
                    <v-col cols="6" sm="6" md="6">
                      <h3>{{ $t('components.ModelSettings.primaryColor') }}</h3>
                      <br>
                      <v-text-field v-model="primaryColor" :rules="primaryColorRules" :label="$t('components.ModelSettings.primaryColorLabel')"></v-text-field>
                      <v-icon :color="primaryColor" v-if="primaryColor">
                        mdi-square
                      </v-icon>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="4" md="4">
                      <h3>{{ $t('components.ModelSettings.logo') }}</h3>
                      <br>
                      <input v-show="false" type="file" accept="image/png, image/jpeg" @change="previewFile" id="file" ref="file" />
                      <v-btn color="primary" dark small @click="selectFile">
                        {{ $t('components.ModelSettings.chooseLogoFile') }}
                      </v-btn>
                      <span class="ml-5" v-if="file">{{ !!file ? file.name : '' }} ({{ fileSizeToString(file.size) }})</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <img :src="logoUrl" alt="" height="60">
                      <v-tooltip bottom v-if="logoUrl">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon @click="logoUrl = null" style="margin-bottom: 10px;">
                            <v-icon color="red" v-if="logoUrl" v-on="on">
                              mdi-delete
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('components.ModelSettings.deleteLogo') }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row v-if="kpiOptions.length > 1">
                    <v-col cols="12" sm="6" md="6">
                      <h3>{{ $t('components.ModelSettings.hiddenKpis') }}</h3>
                      <br>
                      <v-autocomplete
                        v-model="hiddenKpis"
                        :items="kpiOptions"
                        :label="$t('components.ModelSettings.hiddenKpisLabel')"
                        :hint="$t('components.ModelSettings.hiddenKpisHint')"
                        :rules="hiddenKpisRules"
                        persistent-hint
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        clearable
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item>
                  <v-row class="mt-2">
                    <v-col cols="6">
                      <h3>{{ $t('components.ModelSettings.prettyMediaNames') }}</h3>
                      <br>
                      <v-simple-table class="mt-4">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.originalName') }}
                              </th>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.displayName') }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(value, key) in prettyNames.media" :key="key">
                              <td>{{ key }}</td>
                              <td><v-text-field v-model="prettyNames.media[key]" :rules="mediaPrettyNamesRules"></v-text-field></td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>
                    <v-col cols="6">
                      <h3>{{ $t('components.ModelSettings.customKpiName') }}</h3>
                      <br>
                      <v-text-field v-model="customKpiName" :label="$t('components.ModelSettings.customKpiNameLabel')"></v-text-field>
                      <h3 class="mt-6">
                        {{ $t('components.ModelSettings.prettyExternalVariableNames') }}
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click.stop="addExternalVariablePrettyName">
                              <v-icon small>mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          {{ $t('components.ModelSettings.addExternalVariableName') }}
                        </v-tooltip>
                      </h3>
                      <br>
                      <v-simple-table>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.originalName') }}
                              </th>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.displayName') }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(value, index) in externalVariablesPrettyNamesArray" :key="index">
                              <td><v-text-field v-model="externalVariablesPrettyNamesArray[index].key"></v-text-field></td>
                              <td><v-text-field v-model="externalVariablesPrettyNamesArray[index].alias"></v-text-field></td>
                              <td>
                                <v-tooltip top>
                                  <template v-slot:activator="{ on }">
                                    <v-btn icon v-on="on" @click.stop="deleteExternalVariablePrettyName(index)">
                                      <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                  </template>
                                  {{ $t('components.ModelSettings.delete') }}
                                </v-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                      <h3 class="mt-6">
                        {{ $t('components.ModelSettings.prettyComponentNames') }}
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click.stop="addComponentPrettyName">
                              <v-icon small>mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          {{ $t('components.ModelSettings.addPrettyComponentName') }}
                        </v-tooltip>
                      </h3>
                      <br>
                      <v-simple-table>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.originalName') }}
                              </th>
                              <th class="text-left">
                                {{ $t('components.ModelSettings.displayName') }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(value, index) in componentsPrettyNamesArray" :key="index">
                              <td><v-text-field v-model="componentsPrettyNamesArray[index].key"></v-text-field></td>
                              <td><v-text-field v-model="componentsPrettyNamesArray[index].alias"></v-text-field></td>
                              <td>
                                <v-tooltip top>
                                  <template v-slot:activator="{ on }">
                                    <v-btn icon v-on="on" @click.stop="deleteComponentPrettyName(index)">
                                      <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                  </template>
                                  {{ $t('components.ModelSettings.delete') }}
                                </v-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item>
                  <v-row class="mt-2">
                    <v-col cols="12" sm="6" md="6">
                      <v-autocomplete
                        v-for="field in metadataFields"
                        :key="field.key"
                        v-model="clientMetadata[field.key]"
                        :items="optionsFor(field)"
                        :label="field.label"
                        :loading="metadataLoading"
                        :disabled="metadataLoading"
                        clearable
                      >
                        <template v-slot:append-outer>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn icon small v-on="on" :disabled="metadataLoading" @click="openAddValueDialog(field)">
                                <v-icon>mdi-plus</v-icon>
                              </v-btn>
                            </template>
                            {{ $t('components.ModelSettings.addValueTooltip') }}
                          </v-tooltip>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <!-- eager: the taxonomy editor must be mounted even if the tab is
                     never visited, because save() reads its data through $refs. -->
                <v-tab-item eager>
                  <channel-taxonomy-settings
                    ref="channelTaxonomy"
                    :client="client"
                    :media-pretty-names="prettyNames.media"
                  ></channel-taxonomy-settings>
                </v-tab-item>
              </v-tabs-items>
            </v-container>
          </v-card-text>
        </v-form>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import numbro from 'numbro'
import _ from 'lodash'
import firebase from '@/firebase'
import ChannelTaxonomySettings from '@/components/ChannelTaxonomySettings'

export default {
  components: {
    ChannelTaxonomySettings
  },
  data () {
    return {
      activeTab: 0,
      validForm: true,
      primaryColor: null,
      error: null,
      file: null,
      logoUrl: null,
      uploadDialog: false,
      prettyNames: {
        media: {},
        components: {},
        externalVariables: {}
      },
      // Saved media names of channels not in the current config: not editable,
      // but merged back into pretty_names.media on save so they are not lost.
      hiddenMediaPrettyNames: {},
      componentsPrettyNamesArray: [],
      externalVariablesPrettyNamesArray: [],
      customKpiName: '',
      // Output names hidden from every user (see src/utils/hiddenKpis.js).
      hiddenKpis: [],
      clientMetadata: {
        client: '',
        brand: '',
        category: '',
        'sub-category': '',
        country: ''
      },
      // { <fieldKey>: string[] } available values per metadata field, loaded
      // from the client_metadata_categories collection to feed the dropdowns.
      metadataCategoryOptions: {},
      // { <fieldKey>: string[] } values added via the explicit "Add value" dialog
      // this session; merged into the dropdowns and registered on save.
      extraMetadataOptions: {},
      // True until the client_metadata_categories registry has finished loading.
      // The metadata selects stay disabled/loading meanwhile so a dropdown can't be
      // opened against an empty option list (Vuetify won't refresh an open menu when
      // the items arrive, which made the list appear empty on the first cold load).
      metadataLoading: true,
      addValueDialog: false,
      addValueField: null,
      addValueText: ''
    }
  },
  methods: {
    ...mapActions('storage', [
      'uploadFile',
      'resetUploadFileProgress',
      'getFileDownloadUrl'
    ]),
    ...mapActions('manager', [
      'saveModelSettings',
      'loadClientMetadataCategories',
      'addClientMetadataCategoryValues',
      'addChannelTaxonomyLevelValues'
    ]),
    // Dropdown options for a field: registered values + values added this session
    // + the currently selected value (so a pre-existing value always shows even if
    // it is not part of the registry yet), de-duplicated and sorted.
    optionsFor (field) {
      const key = field.key
      const all = (this.metadataCategoryOptions[key] || []).concat(this.extraMetadataOptions[key] || [])
      const current = this.clientMetadata[key]
      if (current && all.indexOf(current) === -1) {
        all.push(current)
      }
      return Array.from(new Set(all)).sort((a, b) => String(a).localeCompare(String(b)))
    },
    openAddValueDialog (field) {
      this.addValueField = field
      this.addValueText = ''
      this.addValueDialog = true
    },
    confirmAddValue () {
      const value = (this.addValueText || '').trim()
      if (!value || !this.addValueField) {
        return
      }
      // Track the new value so it appears in the dropdown; it is persisted to the
      // registry on save (the save() routine registers any value not already known).
      const key = this.addValueField.key
      if (this.optionsFor(this.addValueField).indexOf(value) === -1) {
        if (!this.extraMetadataOptions[key]) {
          this.$set(this.extraMetadataOptions, key, [])
        }
        this.extraMetadataOptions[key].push(value)
      }
      this.clientMetadata[key] = value
      this.addValueDialog = false
      this.addValueField = null
      this.addValueText = ''
    },
    addComponentPrettyName () {
      this.componentsPrettyNamesArray.push({
        key: 'a',
        alias: 'b'
      })
    },
    deleteComponentPrettyName (index) {
      this.componentsPrettyNamesArray.splice(index, 1)
    },
    addExternalVariablePrettyName () {
      this.externalVariablesPrettyNamesArray.push({
        key: 'a',
        alias: 'b'
      })
    },
    deleteExternalVariablePrettyName (index) {
      this.externalVariablesPrettyNamesArray.splice(index, 1)
    },
    validateMediaPrettyNames (v) {
      let count = 0
      const obj = this.prettyNames.media
      for (let key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] === v) {
          count++
        }
      }
      return count > 1
    },
    close: function () {
      this.$emit('close-dialog')
    },
    validateForm () {
      return this.$refs.form.validate()
    },
    save: async function () {
      if (this.validateForm()) {
        this.error = null
        if (this.file) {
          await this.triggerUploadFile()
            .then(() => {
              console.log('Logo uploaded')
            })
            .catch(error => {
              this.error = this.$t('components.ModelSettings.errorUploadingLogo')
              console.log(error)
            })
          await this.getFileDownloadUrl({
            path: `logos/${this.client.id}.${this.file.type === 'image/png' ? 'png' : 'jpg'}`
          }).then(url => {
            this.logoUrl = url
          })
        }
        let json = {}
        // Logo
        if (this.logoUrl !== this.client.logo) {
          console.log('New logo')
          json['logo'] = this.logoUrl
        }
        // Primary Color
        if (this.primaryColor !== this.client.primary_color) {
          console.log('New Primary Color')
          json['primary_color'] = this.primaryColor
        }
        // Pretty Names
        // Components
        // Validate components pretty names
        this.prettyNames.components = {}
        for (const item of this.componentsPrettyNamesArray) {
          if (item.key.length === 0 || item.alias.length === 0) {
            // Error: empty values
            this.error = this.$t('components.ModelSettings.componentNamesEmptyError')
          }
        }
        if (_.filter(_.groupBy(this.componentsPrettyNamesArray, 'key'), group => group.length >= 2).length > 0) {
          // Error: duplicated keys
          this.error = this.$t('components.ModelSettings.componentNamesDuplicateError')
        }
        // Copy components pretty names
        for (const item of this.componentsPrettyNamesArray) {
          this.prettyNames.components[item.key] = item.alias
        }
        // ExternalVariables
        // Validate ExternalVariables pretty names
        this.prettyNames.externalVariables = {}
        for (const item of this.externalVariablesPrettyNamesArray) {
          if (item.key.length === 0 || item.alias.length === 0) {
            // Error: empty values
            this.error = this.$t('components.ModelSettings.externalVariablesEmptyError')
          }
        }
        if (_.filter(_.groupBy(this.externalVariablesPrettyNamesArray, 'key'), group => group.length >= 2).length > 0) {
          // Error: duplicated keys
          this.error = this.$t('components.ModelSettings.externalVariablesDuplicateError')
        }
        // Copy ExternalVariables pretty names
        for (const item of this.externalVariablesPrettyNamesArray) {
          this.prettyNames.externalVariables[item.key] = item.alias
        }
        // Media (plus the unlisted names of channels no longer in the config)
        json['pretty_names'] = {
          ...this.prettyNames,
          media: { ...this.hiddenMediaPrettyNames, ...this.prettyNames.media }
        }
        // KPI
        if (this.customKpiName.length > 0) {
          json['customKpiName'] = this.customKpiName
        } else {
          json['customKpiName'] = null
        }
        // Hidden KPIs — only names still offered, so an output removed from the config
        // does not linger as a hidden KPI (and drop the Total) with no way to un-hide it.
        const hiddenKpis = this.hiddenKpis.filter(name => this.kpiOptions.indexOf(name) !== -1)
        json['hidden_kpis'] = hiddenKpis.length > 0 ? hiddenKpis : firebase.firestore.FieldValue.delete()
        // Client Metadata
        // Write each field with a dot-notation path so other keys in the
        // client_metadata map (e.g. those set by the import script) are kept.
        // Collect any value that is not already a registered option so it can be
        // added to the client_metadata_categories registry on save.
        const newCategoryValues = {}
        for (const field of this.metadataFields) {
          const value = String(this.clientMetadata[field.key] || '').trim()
          if (value.length > 0) {
            json[`client_metadata.${field.key}`] = value
            const existing = this.metadataCategoryOptions[field.key] || []
            if (!existing.includes(value)) {
              newCategoryValues[field.key] = [value]
            }
          } else {
            json[`client_metadata.${field.key}`] = firebase.firestore.FieldValue.delete()
          }
        }
        // Channel Taxonomy — written as a whole map in one field (channel keys may
        // contain dots, which would corrupt dot-notation string field paths). The
        // editor component builds the map and reports values not yet in the registry.
        const taxonomyData = this.$refs.channelTaxonomy.buildSaveData()
        const newTaxonomyValues = taxonomyData.newTaxonomyValues
        json['channel_taxonomy'] = Object.keys(taxonomyData.taxonomyMap).length > 0
          ? taxonomyData.taxonomyMap
          : firebase.firestore.FieldValue.delete()
        if (!this.error) {
          // Register newly created category values first (non-fatal: a registry
          // failure must not block saving the model itself).
          if (Object.keys(newCategoryValues).length > 0) {
            await this.addClientMetadataCategoryValues({
              valuesByField: newCategoryValues,
              uid: this.authUser.uid,
              performedByEmail: this.authUser.email,
              clientId: this.client.id
            }).catch(error => {
              console.log('Error registering new metadata category values', error)
            })
          }
          // Same best-effort semantics for new taxonomy level values.
          if (Object.keys(newTaxonomyValues).length > 0) {
            await this.addChannelTaxonomyLevelValues({
              valuesByLevel: newTaxonomyValues,
              uid: this.authUser.uid,
              performedByEmail: this.authUser.email,
              clientId: this.client.id
            }).catch(error => {
              console.log('Error registering new channel taxonomy values', error)
            })
          }
          this.saveModelSettings({
            client: this.client.id,
            json: json
          }).then(() => {
            this.close()
          }).catch(error => {
            this.error = this.$t('components.ModelSettings.errorUpdatingSettings')
            console.log(error)
          })
        } else {
          console.log(this.error)
          console.log(json.logo)
          console.log('No settings changed')
        }
      }
    },
    selectFile: function () {
      this.$refs.file.click()
    },
    previewFile: function () {
      this.file = this.$refs.file.files[0]
    },
    triggerUploadFile: function () {
      return new Promise((resolve, reject) => {
        this.uploadDialog = true
        this.uploadFile({
          file: this.file,
          path: `logos/${this.client.id}.${this.file.type === 'image/png' ? 'png' : 'jpg'}`
        }).then(() => {
          this.uploadDialog = false
          resolve()
        }, error => {
          reject(error)
        })
      })
    },
    fileSizeToString: function (size) {
      return numbro(size).format({
        output: 'byte',
        base: 'binary',
        mantissa: 1
      })
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('storage', [
      'uploadFileProgress'
    ]),
    primaryColorRules () {
      return [
        // eslint-disable-next-line
        v => (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) || !v || v.length === 0) || this.$t('components.ModelSettings.invalidPrimaryColor')
      ]
    },
    mediaPrettyNamesRules () {
      return [
        // eslint-disable-next-line
        v => !!v || this.$t('components.ModelSettings.required'),
        v => !this.validateMediaPrettyNames(v) || this.$t('components.ModelSettings.duplicatedMediaName')
      ]
    },
    // Every output of the config(s) — plans run on plan_reference_model when present,
    // so its outputs count too.
    kpiOptions () {
      const names = {}
      const configs = [this.client.reference_model, this.client.plan_reference_model]
      configs.forEach(refModel => {
        if (refModel && refModel.config && refModel.config.outputs) {
          Object.keys(refModel.config.outputs).forEach(name => {
            names[name] = true
          })
        }
      })
      return Object.keys(names)
    },
    hiddenKpisRules () {
      return [
        v => (v || []).length < this.kpiOptions.length || this.$t('components.ModelSettings.hiddenKpisAtLeastOne')
      ]
    },
    metadataFields () {
      return [
        { key: 'client', label: this.$t('components.ModelSettings.metadataClient') },
        { key: 'brand', label: this.$t('components.ModelSettings.metadataBrand') },
        { key: 'category', label: this.$t('components.ModelSettings.metadataCategory') },
        { key: 'sub-category', label: this.$t('components.ModelSettings.metadataSubCategory') },
        { key: 'country', label: this.$t('components.ModelSettings.metadataCountry') }
      ]
    }
  },
  props: {
    client: {
      type: Object,
      required: true
    },
    initialTab: {
      type: Number,
      default: 0
    }
  },
  created: function () {
    this.activeTab = this.initialTab
    this.resetUploadFileProgress()
    // Primary Color
    if (this.client.primary_color) {
      this.primaryColor = this.client.primary_color
    }
    // Logo
    if (this.client.logo) {
      this.logoUrl = this.client.logo
    }
    // Pretty Names
    // Media
    // One row per channel of the current config(s), so channels added after the
    // names were first saved still show up; saved names are applied on top.
    // Plans run on plan_reference_model when present, so its channels count too.
    // A model can also exist without any config (e.g. an Ad-Hoc model registered
    // without its JSON), in which case there are no channels to list.
    const savedMedia = (this.client.pretty_names && this.client.pretty_names.media) || {}
    const channels = {}
    const configs = [this.client.reference_model, this.client.plan_reference_model]
    configs.forEach(refModel => {
      if (refModel && refModel.config && refModel.config.media) {
        Object.keys(refModel.config.media).forEach(channel => {
          channels[channel] = true
        })
      }
    })
    const prettyNamesMedia = {}
    Object.keys(channels).forEach(channel => {
      prettyNamesMedia[channel] = savedMedia[channel] || channel
    })
    this.prettyNames.media = prettyNamesMedia
    // Names saved for channels no longer in the config are not listed, but are
    // kept on save: older plans and reports may still contain those channels.
    Object.keys(savedMedia).forEach(channel => {
      if (!channels[channel]) {
        this.hiddenMediaPrettyNames[channel] = savedMedia[channel]
      }
    })
    // Components
    if (this.client.pretty_names && this.client.pretty_names.components) {
      for (const key in this.client.pretty_names.components) {
        this.componentsPrettyNamesArray.push({
          key: key,
          alias: this.client.pretty_names.components[key]
        })
      }
    }
    // External Variables
    if (this.client.pretty_names && this.client.pretty_names.externalVariables) {
      for (const key in this.client.pretty_names.externalVariables) {
        this.externalVariablesPrettyNamesArray.push({
          key: key,
          alias: this.client.pretty_names.externalVariables[key]
        })
      }
    }
    // Custom KPI
    if (this.client.customKpiName) {
      this.customKpiName = this.client.customKpiName
    }
    // Hidden KPIs (a copy, so the dialog never edits the store's array in place)
    if (Array.isArray(this.client.hidden_kpis)) {
      this.hiddenKpis = this.client.hidden_kpis.slice()
    }
    // Client Metadata
    if (this.client.client_metadata) {
      this.metadataFields.forEach(field => {
        const value = this.client.client_metadata[field.key]
        if (value !== undefined && value !== null) {
          this.clientMetadata[field.key] = value
        }
      })
    }
    // Available values per metadata field (for the dropdowns). An empty map just
    // means no preset options yet; users can still type new values.
    this.loadClientMetadataCategories().then(categories => {
      this.metadataCategoryOptions = categories
    }).catch(error => {
      console.log('Error loading metadata category options', error)
    }).finally(() => {
      this.metadataLoading = false
    })
  }
}
</script>
